/**
 * Public guide pages (7절 유입). Search phrases come from Naver autocomplete
 * checked on 2026-10-16 (plans/sobidogam.md 7절). Pure data: imported by the
 * browser app and by the build-time prerender, so no window/CSS here.
 */

export interface GuideSection {
	id?: string;
	heading: string;
	paragraphs?: string[];
	steps?: string[];
	list?: string[];
	examples?: string[];
	table?: { head: string[]; rows: string[][] };
}

export interface Guide {
	slug: string;
	title: string;
	description: string;
	h1: string;
	lede: string;
	sections: GuideSection[];
	cta: { label: string; href: string };
	secondary?: { label: string; href: string };
}

/** Real notification formats the parser reads (domain index.test.ts vectors). */
export const NOTIFICATION_EXAMPLES = [
	"KB국민카드 08/07 09:01 스타벅스 5,100원 승인",
	"현대카드 08/07 09:02 현대백화점 32,000원 일시불",
	"삼성카드 08/07 09:03 CU 강남점 3,200원 승인",
	"토스 알림 08/07 09:05 배달의민족 22,000원 결제",
	"NH농협카드 08/07 09:08 동네마트 44,500원 승인",
] as const;

export const guides: Guide[] = [
	{
		slug: "iphone-auto-entry",
		title: "아이폰 가계부 자동입력 · 소비도감",
		description:
			"아이폰은 문자를 앱에 넘기지 못해요. 결제 알림을 한 번 붙여 넣거나 단축어 한 번으로 가계부에 기록하는 법.",
		h1: "아이폰 가계부 자동입력, 붙여 넣기 한 번으로",
		lede: "아이폰은 카드 문자를 다른 앱이 읽지 못하게 막아요. 그래서 어느 가계부든 복사는 한 번 필요해요. 소비도감은 그 한 번 뒤를 전부 자동으로 채워요.",
		sections: [
			{
				heading: "왜 아이폰은 자동으로 안 될까요",
				paragraphs: [
					"안드로이드는 문자 앱이 알림을 다른 앱에 넘길 수 있지만, 아이폰은 개인정보 보호 때문에 문자 내용을 앱에 주지 않아요. 문자 자동 인식을 내세우는 가계부도 아이폰에서는 '문자 복사 → 붙여 넣기'를 따로 안내해요.",
					"소비도감은 복사 한 번을 받아서 매장·금액·시각·분류를 한 번에 채우고, 확인만 누르면 저장돼요.",
				],
			},
			{
				heading: "붙여 넣기 한 번으로 기록하기",
				steps: [
					"결제 문자나 카드 앱 알림을 길게 눌러 복사해요.",
					"소비도감에서 '결제 알림 붙여 넣기'를 눌러요.",
					"매장·금액·날짜와 추정한 분류를 확인하고 '도감에 넣기'를 눌러요.",
				],
				paragraphs: [
					"여러 건을 한꺼번에 복사해도 돼요. 빈 줄로 나뉜 알림을 각각 한 건으로 읽어요. 취소 알림은 기록하지 않아요.",
				],
			},
			{
				heading: "이런 알림을 읽어요",
				paragraphs: ["아래 문장은 실제로 읽히는지 매번 자동 시험을 거쳐요."],
				examples: [...NOTIFICATION_EXAMPLES],
			},
			{
				id: "shortcut",
				heading: "단축어로 한 번에 넣기",
				paragraphs: [
					"아이폰 기본 앱 '단축어'로 복사한 알림을 바로 기록 화면에 채울 수 있어요. 알림 원문은 주소의 # 뒤에만 실려 서버로 가지 않고, 화면이 열리면 주소창에서 바로 지워져요.",
				],
				steps: [
					"단축어 앱에서 새 단축어를 만들고 '클립보드 가져오기'를 추가해요.",
					"'텍스트 URL 인코딩'을 추가해 클립보드를 인코딩해요.",
					"'URL 열기'를 추가하고 주소에 https://sobidogam.com/app/capture#paste= 를 적은 뒤 바로 뒤에 인코딩한 텍스트를 넣어요.",
					"이름을 '소비 기록'으로 저장하고 홈 화면이나 뒷면 탭에 두세요.",
				],
			},
			{
				heading: "안드로이드라면 더 짧아요",
				paragraphs: [
					"소비도감을 홈 화면에 추가하면 문자 앱의 '공유' 목록에 소비도감이 생겨요. 결제 문자를 공유하면 확인 화면이 바로 열리고, 저장까지 한 번만 누르면 돼요.",
				],
			},
		],
		cta: { label: "결제 알림 붙여 넣어 보기", href: "/app/capture" },
	},
	{
		slug: "move-from-other-ledger",
		title: "위플·편한가계부 백업 옮기기 · 소비도감",
		description:
			"위플 가계부, 편한가계부에서 내보낸 파일을 소비도감으로 옮기는 방법. 중복은 자동으로 건너뛰어요.",
		h1: "쓰던 가계부 기록, 그대로 옮겨 와요",
		lede: "새 가계부로 바꾸면 지난 기록이 끊기는 게 가장 아까워요. 쓰던 앱에서 파일을 한 번 내보내면 소비도감이 날짜·매장·금액·분류를 그대로 읽어요.",
		sections: [
			{
				heading: "1. 쓰던 앱에서 파일 내보내기",
				paragraphs: [
					"위플 가계부와 편한가계부는 설정의 백업·내보내기 메뉴에서 엑셀(CSV) 파일을 만들 수 있어요. 앱 버전에 따라 메뉴 이름이 조금 다를 수 있어요. 파일을 휴대폰에 저장하거나 나에게 보내 두세요.",
				],
			},
			{
				heading: "2. 소비도감에서 가져오기",
				steps: [
					"소비도감 '새 기록' → '알림·파일 가져오기'를 눌러요.",
					"내보낸 CSV 파일을 골라요.",
					"미리 보기에서 건수와 금액을 확인하고 가져와요.",
				],
				paragraphs: [
					"날짜·내용(가맹점)·금액·분류 열 이름을 알아서 찾아요. 수입과 이체 줄은 빼고 지출만 가져와요. 같은 파일을 두 번 가져와도 이미 있는 기록은 건너뛰어요.",
				],
			},
			{
				heading: "열 이름이 다르면",
				paragraphs: [
					"은행·카드사 엑셀이나 직접 만든 표처럼 열 이름이 다르면 '다른 형식'에서 날짜·금액·내용 열을 직접 고를 수 있어요.",
				],
			},
			{
				heading: "옮긴 뒤에 할 일",
				list: [
					"'기타'로 들어온 기록은 내역에서 여러 건을 골라 한 번에 분류를 바꿔요.",
					"한 번 바꾼 매장 분류는 다음 기록부터 기억해요.",
					"월급날이 1일이 아니면 설정에서 월 시작일을 바꾸세요. 리포트와 한도가 같이 바뀌어요.",
				],
			},
		],
		cta: { label: "파일 가져오러 가기", href: "/app/capture" },
	},
	{
		slug: "excel-ledger",
		title: "가계부 엑셀 양식 무료 · 소비도감",
		description:
			"날짜·매장·금액·분류 4열 가계부 엑셀 양식을 무료로 받아 쓰고, 쓰던 표는 그대로 소비도감으로 옮겨요.",
		h1: "가계부 엑셀 양식, 무료로 받아 가세요",
		lede: "엑셀 가계부는 자유롭지만 합계 수식과 월 경계를 매번 손으로 맞춰야 해요. 4열 양식을 그대로 쓰다가 언제든 소비도감으로 옮길 수 있어요.",
		sections: [
			{
				heading: "양식 구성",
				table: {
					head: ["날짜", "매장", "금액", "분류"],
					rows: [
						["2026-10-01", "동네마트", "12300", "마트"],
						["2026-10-02", "스타벅스", "5800", "카페"],
						["2026-10-03", "넷플릭스", "17000", "구독"],
					],
				},
				paragraphs: [
					"엑셀·넘버스·구글 시트에서 열 수 있는 CSV 파일이에요. 금액은 숫자만 적고, 분류는 비워 둬도 돼요.",
				],
			},
			{
				heading: "엑셀로 할 때 손으로 하는 일",
				table: {
					head: ["할 일", "엑셀", "소비도감"],
					rows: [
						[
							"한 건 입력",
							"날짜·매장·금액·분류 4칸",
							"결제 알림 붙여 넣기 1번",
						],
						[
							"월 합계",
							"SUM 수식, 월마다 범위 수정",
							"자동, 월급날 기준도 가능",
						],
						["분류 비율", "피벗 표·차트 직접", "자동 그래프"],
						[
							"카드 명세와 대조",
							"눈으로 비교",
							"합계 하나 넣으면 빈 날짜 표시",
						],
					],
				},
			},
			{
				heading: "쓰던 표를 소비도감으로",
				steps: [
					"엑셀에서 '다른 이름으로 저장' → CSV(UTF-8)로 저장해요.",
					"소비도감 '알림·파일 가져오기'에서 파일을 골라요.",
					"열 이름이 양식과 다르면 '다른 형식'에서 날짜·금액·내용 열을 골라요.",
				],
			},
		],
		cta: {
			label: "무료 양식 내려받기",
			href: "/downloads/sobidogam-ledger-template.csv",
		},
		secondary: { label: "쓰던 표 가져오기", href: "/app/capture" },
	},
	{
		slug: "household-restock",
		title: "생필품 리스트와 장보기 리스트 · 소비도감",
		description:
			"휴지·세제·샴푸처럼 자주 떨어지는 생필품 리스트. 다 썼어 한 번이면 장보기에 들어가고 영수증으로 완료돼요.",
		h1: "생필품 리스트, 다 쓴 순간 장보기로",
		lede: "휴지가 떨어진 걸 알아챈 순간 적어 두지 않으면 마트에서 또 잊어요. 소비도감은 '다 썼어' 한 번을 장보기 목록으로, 산 영수증을 다시 재고로 이어요.",
		sections: [
			{
				heading: "자주 떨어지는 생필품",
				list: [
					"욕실: 두루마리 휴지, 샴푸, 바디워시, 치약, 칫솔",
					"세탁: 세탁 세제, 섬유유연제, 얼룩 제거제",
					"부엌: 주방 세제, 수세미, 키친타월, 비닐백, 쌀",
					"생활: 물티슈, 건전지, 쓰레기봉투",
				],
			},
			{
				heading: "다 썼어 → 장보기 → 완료",
				steps: [
					"장보기 화면에 집에 두는 물건을 등록해요.",
					"떨어지면 '다 썼어'를 눌러요. 장보기 목록에 바로 들어가요.",
					"장을 보고 결제 알림을 기록하면서 산 물건을 연결하면 목록이 완료되고 재고가 다시 '있어요'가 돼요.",
				],
			},
			{
				heading: "떨어질 날을 미리 알려 드려요",
				paragraphs: [
					"같은 물건을 두 번 이상 사면 평균 간격으로 다음에 떨어질 날을 계산해요. 7일 안으로 다가오면 홈에 '이번 주 살 것'으로 보이고, 한 번 누르면 장보기에 담겨요.",
				],
			},
			{
				heading: "가족에게 부탁하기",
				paragraphs: [
					"장보기 목록을 카톡·문자로 보낼 수 있어요. 받는 사람은 앱이 없어도 목록을 읽을 수 있어요.",
				],
			},
		],
		cta: { label: "첫 물건 등록하기", href: "/app/household" },
	},
	{
		slug: "living-cost",
		title: "생활비 관리방법, 월급날 기준으로 · 소비도감",
		description:
			"월급날 기준 월 시작일, 식비·생활용품·반복비 한도, 카드 명세와 맞춰 보기로 생활비를 관리하는 방법.",
		h1: "생활비 관리, 월급날부터 다음 월급날까지",
		lede: "1일 기준 가계부는 25일 월급을 받는 집과 맞지 않아요. 월 시작일을 월급날로 두면 합계·한도·리포트가 같은 기간으로 움직여요.",
		sections: [
			{
				heading: "1. 월 시작일을 월급날로",
				paragraphs: [
					"설정에서 월 시작일을 1~28일 중 고를 수 있어요. 25일로 두면 '이번 달'이 9월 25일~10월 24일이 되고, 내역·리포트·한도가 모두 이 기간을 따라요.",
				],
			},
			{
				heading: "2. 세 갈래 한도와 분류 한도",
				list: [
					"먹고 마신 생활비, 집에서 쓰는 생활용품, 매달 나가는 반복비 세 갈래에 월 한도를 둬요.",
					"카페·배달처럼 새는 분류 하나에도 따로 한도를 둘 수 있어요. 결제를 기록하는 순간 '카페 한도 44,200원 남았어요'처럼 바로 알려 줘요.",
				],
			},
			{
				heading: "3. 카드 명세와 맞춰 보기",
				paragraphs: [
					"카드 앱에서 본 이번 달 합계를 하나만 넣으면 기록 합계와의 차이와 기록이 없는 날짜를 보여 줘요. 빠진 결제를 그 자리에서 붙여 넣으면 차이가 줄어요. 할부·해외 결제·취소 시점 때문에 다를 수 있어 참고용이에요.",
				],
			},
			{
				heading: "4. 정기 결제는 달력에",
				paragraphs: [
					"구독·통신비 같은 정기 결제를 예정표에 두면 달력에 결제일과 공휴일이 함께 보여요. 휴대폰 달력에 결제일 알림을 넣을 수도 있어요.",
				],
			},
		],
		cta: { label: "월 시작일 정하기", href: "/app/settings" },
	},
];

export function guideBySlug(slug: string | undefined): Guide | undefined {
	return guides.find((guide) => guide.slug === slug);
}

export const PRICING = {
	title: "소비도감 가격 · 기록은 평생 무료",
	description:
		"기록·리포트·한도·달력·내보내기는 평생 무료예요. 깊은 회고와 소장용 결산만 한 번 구매해요. 광고 없음.",
	free: [
		"결제 알림 붙여 넣기·직접 입력·파일 가져오기, 기록 수 제한 없음",
		"기록 고치기·여러 건 정리·검색·매장별 모아보기",
		"주간·월간·연말 리포트와 분류 그래프, 달력",
		"생활비 세 갈래 한도와 분류 한도, 카드 명세와 맞춰 보기",
		"집 안 물건·장보기·떨어질 날 예측",
		"올해 결산 미리 보기, 기본 공유 카드",
		"CSV·JSON 내보내기, 복구코드 암호화 백업 파일",
	],
	paid: [
		{
			name: "기억 보관함 평생권",
			price: "9,900원",
			kind: "한 번 구매",
			body: "연도별 기억 아카이브, 계절·요일·다시 간 곳 심화 회고, 공유 카드 추가 디자인 4종",
		},
		{
			name: "연간 기억책",
			price: "2,900원",
			kind: "그해 한 번 구매",
			body: "인쇄용 연말 결산 PDF 저장",
		},
	],
	rules: [
		"구독이 아니라 해지할 것이 없어요. 자동으로 결제되지 않아요.",
		"결제는 토스 앱 안의 소비도감에서 열 예정이에요. 지금은 준비 중이라 구매할 수 없어요.",
		"디지털 콘텐츠라 결제 후 열람하면 청약철회가 제한될 수 있어요(전자상거래법 제17조 제2항 제5호). 열람 전이거나 내용이 설명과 다르면 환불받을 수 있어요.",
		"환불은 토스 앱 결제 내역에서 요청하거나 support@munops.com으로 알려 주세요. 다른 기기로 옮기면 '구매 복원'으로 되찾아요.",
	],
};
